/**
 * apple-panel.js
 *
 * The Apple Music library browser — a sidebar for finding and loading music.
 * Transport controls (play/pause/seek/volume/skip) are handled entirely by
 * Webamp's own UI via the WebampController audio proxy.
 *
 * This panel is responsible for:
 *   - Auth button → triggers MusicKit sign-in
 *   - Playlist list → fetches library playlists, expands tracks
 *   - Clicking a track → loads playlist into Webamp + starts MusicKit queue
 *   - Search tab → debounced Apple Music catalog search
 *   - Now-playing strip → album art, title, artist (cosmetic, driven by MusicKit events)
 *   - Open/close toggle
 */

import { MusicKitBridge } from './musickit-bridge.js';

export class ApplePanel {
  /**
   * @param {MusicKitBridge}                             mkBridge
   * @param {import('webamp').default}                   webamp
   * @param {import('./webamp-controller').WebampController} webampController
   */
  constructor(mkBridge, webamp, webampController) {
    this.mk = mkBridge;
    this.webamp = webamp;
    this.ctrl = webampController; // so we can call setPlaylist() on track load

    // Current state
    this._currentPlaylistId = null;
    this._currentPlaylistTracks = [];
    this._searchDebounce = null;

    // DOM refs — populated in init()
    this.panel = null;
    this.toggleBtn = null;
  }

  // ─────────────────────────────────────────────────────────────
  // Bootstrap
  // ─────────────────────────────────────────────────────────────

  init() {
    this.panel = document.getElementById('apple-music-panel');
    this.toggleBtn = document.getElementById('amp-toggle-btn');

    this._bindPanelEvents();
    this._bindMusicKitEvents();

    // If already authorized (e.g. persisted session), skip auth screen
    if (this.mk.isAuthorized) {
      this._showMainScreen();
      this._loadPlaylists();
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Panel open/close
  // ─────────────────────────────────────────────────────────────

  _bindPanelEvents() {
    // Toggle button
    this.toggleBtn.addEventListener('click', () => this._togglePanel());

    // Close button
    document.getElementById('amp-close-btn').addEventListener('click', () => {
      this._hidePanel();
    });

    // Auth button
    document.getElementById('amp-auth-btn').addEventListener('click', async () => {
      const btn = document.getElementById('amp-auth-btn');
      btn.textContent = 'Connecting…';
      btn.disabled = true;

      try {
        await this.mk.authorize();
        this._showMainScreen();
        await this._loadPlaylists();
      } catch (err) {
        console.error('[ApplePanel] Auth failed:', err);
        btn.textContent = 'Connect Apple Music';
        btn.disabled = false;
        this._showError('Sign-in failed. Please try again.');
      }
    });

    // Disconnect button
    document.getElementById('amp-disconnect-btn').addEventListener('click', async () => {
      await this.mk.disconnect();
      this._showAuthScreen();
    });

    // Transport controls (play/pause/seek/volume) are now owned by Webamp's UI.
    // The panel's role is library browsing only.

    // Tab switching
    document.querySelectorAll('.amp-tab').forEach((tab) => {
      tab.addEventListener('click', () => this._switchTab(tab.dataset.tab));
    });

    // Search
    const searchInput = document.getElementById('amp-search-input');
    const searchBtn = document.getElementById('amp-search-btn');

    const doSearch = () => {
      clearTimeout(this._searchDebounce);
      this._searchDebounce = setTimeout(() => {
        this._runSearch(searchInput.value);
      }, 400);
    };

    searchInput.addEventListener('input', doSearch);
    searchBtn.addEventListener('click', () => {
      clearTimeout(this._searchDebounce);
      this._runSearch(searchInput.value);
    });
    searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        clearTimeout(this._searchDebounce);
        this._runSearch(searchInput.value);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // MusicKit event responses
  // ─────────────────────────────────────────────────────────────

  _bindMusicKitEvents() {
    // Update the now-playing strip whenever the track changes.
    // Everything else (scrubber, time, play state) is handled by Webamp
    // reacting to events on the patched audio element.
    this.mk.addEventListener('trackchange', (e) => {
      this._updateNowPlaying(e.detail);
    });

    // Return to auth screen if the session is revoked
    this.mk.addEventListener('disconnected', () => {
      this._showAuthScreen();
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Now Playing
  // ─────────────────────────────────────────────────────────────

  _updateNowPlaying(item) {
    if (!item) return;

    const attr = item.attributes ?? {};
    const nowPlaying = document.getElementById('amp-now-playing');
    const artwork = document.getElementById('amp-artwork');
    const title = document.getElementById('amp-track-title');
    const artist = document.getElementById('amp-track-artist');

    title.textContent = attr.name ?? '—';
    artist.textContent = attr.artistName ?? '—';

    if (attr.artwork?.url) {
      const url = attr.artwork.url
        .replace('{w}', '80')
        .replace('{h}', '80');
      artwork.src = url;
      artwork.style.display = 'block';
    } else {
      artwork.style.display = 'none';
    }

    nowPlaying.classList.remove('amp-now-playing--hidden');

    // Highlight the active track in any visible list
    document.querySelectorAll('.amp-list-item--active').forEach((el) => {
      el.classList.remove('amp-list-item--active');
    });
    const activeId = item.id;
    const activeItem = document.querySelector(`[data-track-id="${activeId}"]`);
    if (activeItem) activeItem.classList.add('amp-list-item--active');
  }

  // ─────────────────────────────────────────────────────────────
  // Playlists tab
  // ─────────────────────────────────────────────────────────────

  async _loadPlaylists() {
    const list = document.getElementById('amp-playlist-list');
    list.innerHTML = '<li class="amp-list-item amp-list-item--loading">Loading…</li>';

    try {
      // Fetch both in parallel
      const [recent, library] = await Promise.all([
        this.mk.getRecentPlaylists().catch(() => []),
        this.mk.getLibraryPlaylists(),
      ]);

      list.innerHTML = '';

      // ── Recently Played section ──────────────────────────────
      if (recent.length) {
        const recentHeader = document.createElement('li');
        recentHeader.className = 'amp-list-item amp-list-header';
        recentHeader.textContent = 'Recently Played';
        list.appendChild(recentHeader);

        recent.forEach((playlist) => {
          list.appendChild(this._buildPlaylistItem(playlist));
        });
      }

      // ── Your Library section ─────────────────────────────────
      const libraryHeader = document.createElement('li');
      libraryHeader.className = 'amp-list-item amp-list-header';
      libraryHeader.textContent = 'Your Library';
      list.appendChild(libraryHeader);

      if (!library.length) {
        const empty = document.createElement('li');
        empty.className = 'amp-list-item amp-list-item--empty';
        empty.textContent = 'No playlists found.';
        list.appendChild(empty);
        return;
      }

      library.forEach((playlist) => {
        list.appendChild(this._buildPlaylistItem(playlist));
      });

    } catch (err) {
      console.error('[ApplePanel] Failed to load playlists:', err);
      list.innerHTML = '<li class="amp-list-item amp-list-item--error">Failed to load playlists.</li>';
    }
  }

  /** Build a playlist <li> element (shared by library + recent sections). */
  _buildPlaylistItem(playlist) {
    const attr = playlist.attributes ?? {};
    const li = document.createElement('li');
    li.className = 'amp-list-item amp-list-item--playlist';
    li.dataset.playlistId = playlist.id;

    const artUrl = attr.artwork?.url
      ? attr.artwork.url.replace('{w}', '32').replace('{h}', '32')
      : null;

    li.innerHTML = `
      ${artUrl ? `<img class="amp-item-art" src="${artUrl}" alt="" />` : '<span class="amp-item-art amp-item-art--placeholder">♫</span>'}
      <span class="amp-item-name">${this._escapeHtml(attr.name ?? 'Untitled Playlist')}</span>
      <span class="amp-item-count">${attr.trackCount != null ? attr.trackCount + ' tracks' : ''}</span>
    `;

    li.addEventListener('click', () => this._onPlaylistClick(playlist, li));
    return li;
  }

  async _onPlaylistClick(playlist, listItem) {
    const playlistId = playlist.id;
    const playlistType = playlist.type; // 'library-playlists' or 'playlists'

    // Toggle expanded sub-list
    const existingSub = listItem.nextElementSibling;
    if (existingSub && existingSub.classList.contains('amp-sublist')) {
      existingSub.remove();
      listItem.classList.remove('amp-list-item--expanded');
      return;
    }

    listItem.classList.add('amp-list-item--expanded');

    const subList = document.createElement('ul');
    subList.className = 'amp-sublist';
    subList.innerHTML = '<li class="amp-list-item amp-list-item--loading">Loading tracks…</li>';
    listItem.after(subList);

    try {
      const tracks = await this.mk.getPlaylistTracks(playlistId, playlistType);

      subList.innerHTML = '';

      if (!tracks.length) {
        subList.innerHTML = '<li class="amp-list-item amp-list-item--empty">Playlist is empty.</li>';
        return;
      }

      this._currentPlaylistId = playlistId;
      this._currentPlaylistTracks = tracks;

      // "Play all" button
      const playAllLi = document.createElement('li');
      playAllLi.className = 'amp-list-item amp-list-item--play-all';
      playAllLi.innerHTML = `▶ Play all (${tracks.length} tracks)`;
      playAllLi.addEventListener('click', (e) => {
        e.stopPropagation();
        this._loadPlaylistIntoWebamp(tracks, playlistId, 0);
        // audio.play() fires via Webamp → controller calls setQueue({ startWith: 0 }) + play()
      });
      subList.appendChild(playAllLi);

      // Individual tracks
      tracks.forEach((track, index) => {
        const attr = track.attributes ?? {};
        const li = document.createElement('li');
        li.className = 'amp-list-item amp-list-item--track';
        li.dataset.trackId = track.id;
        li.dataset.trackIndex = index;

        li.innerHTML = `
          <span class="amp-track-num">${index + 1}</span>
          <span class="amp-track-name">${this._escapeHtml(attr.name ?? '—')}</span>
          <span class="amp-track-dur">${attr.durationInMillis ? this._formatTime(attr.durationInMillis / 1000) : ''}</span>
        `;

        li.addEventListener('click', (e) => {
          e.stopPropagation();
          this._loadPlaylistIntoWebamp(tracks, playlistId, index);
          // audio.play() fires via Webamp → controller calls setQueue({ startWith: index }) + play()
        });

        subList.appendChild(li);
      });
    } catch (err) {
      console.error('[ApplePanel] Failed to load tracks for', playlistId, `(type=${playlistType}):`, err);
      subList.innerHTML = '<li class="amp-list-item amp-list-item--error">Failed to load tracks.</li>';
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Search tab
  // ─────────────────────────────────────────────────────────────

  async _runSearch(query) {
    if (!query.trim()) return;

    const results = document.getElementById('amp-search-results');
    results.innerHTML = '<li class="amp-list-item amp-list-item--loading">Searching…</li>';

    try {
      const { songs, albums } = await this.mk.searchCatalog(query);
      results.innerHTML = '';

      if (!songs.length && !albums.length) {
        results.innerHTML = '<li class="amp-list-item amp-list-item--empty">No results found.</li>';
        return;
      }

      // Songs section
      if (songs.length) {
        const header = document.createElement('li');
        header.className = 'amp-list-item amp-list-header';
        header.textContent = 'Songs';
        results.appendChild(header);

        songs.forEach((song) => {
          const attr = song.attributes ?? {};
          const li = document.createElement('li');
          li.className = 'amp-list-item amp-list-item--track';
          li.dataset.trackId = song.id;

          const artUrl = attr.artwork?.url
            ? attr.artwork.url.replace('{w}', '32').replace('{h}', '32')
            : null;

          li.innerHTML = `
            ${artUrl ? `<img class="amp-item-art" src="${artUrl}" alt="" />` : ''}
            <span class="amp-track-name">${this._escapeHtml(attr.name ?? '—')}</span>
            <span class="amp-track-dur">${this._escapeHtml(attr.artistName ?? '')}</span>
          `;

          li.addEventListener('click', async () => {
            // Load this single catalog song
            this.ctrl.setPlaylist([song], null);
            const webampTrack = MusicKitBridge.toWebampTrack(song);
            this.webamp.setTracksToPlay([webampTrack]);
            await this.mk.playCatalogSong(song.id);
          });

          results.appendChild(li);
        });
      }

      // Albums section
      if (albums.length) {
        const header = document.createElement('li');
        header.className = 'amp-list-item amp-list-header';
        header.textContent = 'Albums';
        results.appendChild(header);

        albums.forEach((album) => {
          const attr = album.attributes ?? {};
          const li = document.createElement('li');
          li.className = 'amp-list-item amp-list-item--playlist';

          const artUrl = attr.artwork?.url
            ? attr.artwork.url.replace('{w}', '32').replace('{h}', '32')
            : null;

          li.innerHTML = `
            ${artUrl ? `<img class="amp-item-art" src="${artUrl}" alt="" />` : '<span class="amp-item-art amp-item-art--placeholder">💿</span>'}
            <span class="amp-item-name">${this._escapeHtml(attr.name ?? '—')}</span>
            <span class="amp-item-count">${this._escapeHtml(attr.artistName ?? '')}</span>
          `;

          li.addEventListener('click', async () => {
            await this.mk.music.setQueue({ album: album.id });
            await this.mk._safePlay();
          });

          results.appendChild(li);
        });
      }
    } catch (err) {
      console.error('[ApplePanel] Search failed:', err);
      results.innerHTML = '<li class="amp-list-item amp-list-item--error">Search failed.</li>';
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Webamp playlist sync
  // ─────────────────────────────────────────────────────────────

  /**
   * Push Apple Music tracks into Webamp's playlist display and tell the
   * controller where to start playback.
   *
   * All queue setup (setQueue + startWith) is deferred to audio.play() inside
   * WebampController — this method just primes the state and lets Webamp's
   * auto-play trigger it naturally.
   *
   * @param {Array}  tracks      Apple Music song resources
   * @param {string} playlistId  Apple Music playlist ID
   * @param {number} startIndex  Which track to start at (default 0)
   */
  _loadPlaylistIntoWebamp(tracks, playlistId, startIndex = 0) {
    // 1. Register the ID mapping + mark queue as needing setup
    this.ctrl.setPlaylist(tracks, playlistId);

    // 2. Set the desired start position (setPlaylist resets it, so set it after)
    this.ctrl.pendingStartIndex = startIndex;

    // 3. Load tracks into Webamp — triggers audio.play() which reads the flags above
    const webampTracks = tracks.map(MusicKitBridge.toWebampTrack);
    this.webamp.setTracksToPlay(webampTracks);
  }

  // ─────────────────────────────────────────────────────────────
  // Visibility helpers
  // ─────────────────────────────────────────────────────────────

  _togglePanel() {
    if (this.panel.classList.contains('amp-panel--hidden')) {
      this._showPanel();
    } else {
      this._hidePanel();
    }
  }

  _showPanel() {
    this.panel.classList.remove('amp-panel--hidden');
    this.toggleBtn.classList.add('amp-toggle-btn--active');
  }

  _hidePanel() {
    this.panel.classList.add('amp-panel--hidden');
    this.toggleBtn.classList.remove('amp-toggle-btn--active');
  }

  _showMainScreen() {
    document.getElementById('amp-auth-screen').classList.add('amp-screen--hidden');
    document.getElementById('amp-main-screen').classList.remove('amp-screen--hidden');
  }

  _showAuthScreen() {
    document.getElementById('amp-main-screen').classList.add('amp-screen--hidden');
    document.getElementById('amp-auth-screen').classList.remove('amp-screen--hidden');
    const btn = document.getElementById('amp-auth-btn');
    btn.textContent = 'Connect Apple Music';
    btn.disabled = false;
    // Clear playlist list so it reloads fresh on next sign-in
    document.getElementById('amp-playlist-list').innerHTML = '';
  }

  _switchTab(tabName) {
    document.querySelectorAll('.amp-tab').forEach((t) => {
      t.classList.toggle('amp-tab--active', t.dataset.tab === tabName);
    });
    document.querySelectorAll('.amp-tab-content').forEach((c) => {
      c.classList.toggle('amp-tab-content--hidden', c.id !== `amp-tab-${tabName}`);
    });
  }

  _showError(msg) {
    // Simple inline error — you can improve this
    console.warn('[ApplePanel]', msg);
    const el = document.createElement('p');
    el.style.cssText = 'color:#ff4444;font-size:11px;margin:4px 8px;';
    el.textContent = msg;
    document.getElementById('amp-auth-screen').appendChild(el);
    setTimeout(() => el.remove(), 4000);
  }

  // ─────────────────────────────────────────────────────────────
  // Utilities
  // ─────────────────────────────────────────────────────────────

  _formatTime(secs) {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  _escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
